import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigableProps } from '../utils/navigation';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  alpha,
  useTheme,
  TextField,
  MenuItem,
  Select,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { format } from 'date-fns';
import PageHeader from '../components/common/PageHeader';
import TableSkeleton from '../components/common/TableSkeleton';
import { projekteService } from '../services/projekteService';
import angeboteService from '../services/angeboteService';
import { statusService } from '../services/lookupService';
import { useSearchParamsState } from '../hooks/useSearchParamsState';
import { usePermissions } from '../hooks/usePermissions';
import * as P from '../constants/permissions';
import { AngebotList, ProjektList, Status } from '../types';
import { isProjectClosedStatus } from '../utils/workflowStatus';
import {
  angebotOhneProjektSichtbar,
  angebotPipelineLabel,
  boardRowKey,
  isFestpreisProjektTyp,
  mapAngebotToProjektRow,
  normalizeProjektTyp,
  projektTypAccent,
  projektTypLabel,
} from '../utils/projektTyp';

type SortField = 'name' | 'projekt_typ' | 'kunde' | 'projektleiter' | 'status' | 'personentage' | 'erstellt_am';

interface Filters {
  name: string;
  projekt_typ: string;
  kunde: string;
  projektleiter: string;
  status: string;
  personentage: string;
  erstellt_am: string;
}

/* ─── Komponente ─── */
const ProjekteListPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  const canViewAngebote = hasPermission(P.ANGEBOTE_VIEW);

  /* Daten */
  const [projekte, setProjekte] = useState<ProjektList[]>([]);
  const [angebote, setAngebote] = useState<AngebotList[]>([]);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [loading, setLoading] = useState(true);

  /* Filter + Sort via URL-Params */
  const [params, setParams, resetParams, hasActiveFilters] = useSearchParamsState({
    defaults: {
      name: '',
      typ: '',
      kunde: '',
      pl: '',
      status: '',
      pt: '',
      erstellt: '',
      sort: '',
      abgeschlossen: false as boolean,
    },
    paramNames: {},
  });

  // Abgeleitete Werte aus URL-Params
  const filters: Filters = {
    name: params.name as string,
    projekt_typ: params.typ as string,
    kunde: params.kunde as string,
    projektleiter: params.pl as string,
    status: params.status as string,
    personentage: params.pt as string,
    erstellt_am: params.erstellt as string,
  };
  const showAbgeschlossen = params.abgeschlossen as boolean;

  // Sort aus URL-Param parsen (Format: "field:direction")
  const sortParts = (params.sort as string).split(':');
  const sortField: SortField | null = sortParts[0] ? (sortParts[0] as SortField) : null;
  const sortDirection: 'asc' | 'desc' = sortParts[1] === 'desc' ? 'desc' : 'asc';

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [projekteData, statusesData, angeboteData] = await Promise.all([
          projekteService.list(),
          statusService.list(),
          canViewAngebote ? angeboteService.list() : Promise.resolve([] as AngebotList[]),
        ]);
        setProjekte(projekteData);
        setStatuses(statusesData);
        setAngebote(angeboteData);
      } catch (error) {
        console.error('Fehler beim Laden der Projekte:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [canViewAngebote]);

  /* ─── Sort-Handler ─── */
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setParams({ sort: `${field}:desc` });
      } else {
        // Reset zu Standardsortierung
        setParams({ sort: '' });
      }
    } else {
      setParams({ sort: `${field}:asc` });
    }
  };

  /* ─── Filter-Handler ─── */
  const FILTER_KEY_MAP: Record<keyof Filters, string> = {
    name: 'name',
    projekt_typ: 'typ',
    kunde: 'kunde',
    projektleiter: 'pl',
    status: 'status',
    personentage: 'pt',
    erstellt_am: 'erstellt',
  };

  const handleFilterChange = (field: keyof Filters, value: string) => {
    setParams({ [FILTER_KEY_MAP[field]]: value } as any);
  };

  /* ─── Daten-Pipeline: Filtern + Sortieren ─── */
  const displayedProjekte = useMemo(() => {
    const offerRows = angebote
      .filter((a) => angebotOhneProjektSichtbar(a, showAbgeschlossen))
      .map(mapAngebotToProjektRow);
    const projectRows: ProjektList[] = projekte.map((p) => ({ ...p, source: 'projekt' as const }));
    let result = [...offerRows, ...projectRows];

    // 1. Abgeschlossene und abgebrochene Projekte ausblenden (wenn Toggle aus)
    if (!showAbgeschlossen) {
      result = result.filter((p) => p.source === 'angebot' || !isProjectClosedStatus(p.status?.name));
    }

    // 2. Spaltenfilter anwenden
    if (filters.name) {
      const q = filters.name.toLowerCase();
      result = result.filter((p) => p.name.toLowerCase().includes(q));
    }
    if (filters.projekt_typ) {
      result = result.filter((p) => normalizeProjektTyp(p.projekt_typ) === filters.projekt_typ);
    }
    if (filters.kunde) {
      const q = filters.kunde.toLowerCase();
      result = result.filter((p) =>
        (p.kunde?.name_des_unternehmens || '').toLowerCase().includes(q),
      );
    }
    if (filters.projektleiter) {
      const q = filters.projektleiter.toLowerCase();
      result = result.filter((p) => {
        const fullName = p.projektleiter
          ? `${p.projektleiter.vorname} ${p.projektleiter.nachname}`
          : '';
        return fullName.toLowerCase().includes(q);
      });
    }
    if (filters.status) {
      result = result.filter((p) => p.status?.name === filters.status);
    }
    if (filters.personentage) {
      const num = parseFloat(filters.personentage);
      if (!isNaN(num)) {
        result = result.filter((p) => p.personentage === num);
      }
    }
    if (filters.erstellt_am) {
      const q = filters.erstellt_am.toLowerCase();
      result = result.filter((p) =>
        format(new Date(p.erstellt_am), 'dd.MM.yyyy').includes(q),
      );
    }

    // 3. Sortierung
    if (sortField) {
      result.sort((a, b) => {
        let cmp = 0;
        switch (sortField) {
          case 'name':
            cmp = a.name.localeCompare(b.name, 'de');
            break;
          case 'projekt_typ':
            cmp = (a.projekt_typ || '').localeCompare(b.projekt_typ || '', 'de');
            break;
          case 'kunde':
            cmp = (a.kunde?.name_des_unternehmens || '').localeCompare(
              b.kunde?.name_des_unternehmens || '',
              'de',
            );
            break;
          case 'projektleiter': {
            const nameA = a.projektleiter
              ? `${a.projektleiter.nachname} ${a.projektleiter.vorname}`
              : '';
            const nameB = b.projektleiter
              ? `${b.projektleiter.nachname} ${b.projektleiter.vorname}`
              : '';
            cmp = nameA.localeCompare(nameB, 'de');
            break;
          }
          case 'status':
            cmp = (a.status?.sort_order ?? 99) - (b.status?.sort_order ?? 99);
            break;
          case 'personentage':
            cmp = (a.personentage || 0) - (b.personentage || 0);
            break;
          case 'erstellt_am':
            cmp = new Date(a.erstellt_am).getTime() - new Date(b.erstellt_am).getTime();
            break;
        }
        return sortDirection === 'asc' ? cmp : -cmp;
      });
    } else {
      // Standardsortierung: Status sort_order, dann erstellt_am desc
      result.sort((a, b) => {
        const prioA = a.status?.sort_order ?? 99;
        const prioB = b.status?.sort_order ?? 99;
        if (prioA !== prioB) return prioA - prioB;
        return new Date(b.erstellt_am).getTime() - new Date(a.erstellt_am).getTime();
      });
    }

    return result;
  }, [projekte, angebote, filters, sortField, sortDirection, showAbgeschlossen]);

  /* ─── Navigation ─── */
  const handleCreateClick = () => {
    navigate('/projekte/neu');
  };

  /* ─── Status-Farbe ─── */
  const getStatusColor = (statusName: string) => {
    switch (statusName) {
      case 'Angebot':
        return 'secondary';
      case 'Geplant':
        return 'info';
      case 'In Bearbeitung':
        return 'warning';
    case 'Abgeschlossen':
    case 'Abgerechnet':
      return 'success';
      case 'Abgebrochen':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return <TableSkeleton columns={7} rows={8} />;
  }

  /* ─── Hilfsfunktion: Sortierbare Spalte ─── */
  const renderSortLabel = (field: SortField, label: string, align?: 'center') => (
    <TableSortLabel
      active={sortField === field}
      direction={sortField === field ? sortDirection : 'asc'}
      onClick={() => handleSort(field)}
      sx={align === 'center' ? { '& .MuiTableSortLabel-icon': { ml: 0.5 } } : undefined}
    >
      {label}
    </TableSortLabel>
  );

  const filterInputSx = {
    '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8125rem' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(theme.palette.divider, 0.3) },
  };

  return (
    <Box>
      <PageHeader
        title="Projekte"
        breadcrumbs={[{ label: 'Projekte' }]}
        onAdd={handleCreateClick}
        addLabel="Neu erstellen"
      />

      {/* Toggle Abgeschlossene + Reset-Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, ml: 0.5 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showAbgeschlossen}
              onChange={(e) => setParams({ abgeschlossen: e.target.checked })}
              size="small"
            />
          }
          label="Abgeschlossene anzeigen"
          slotProps={{ typography: { variant: 'body2', color: 'text.secondary' } }}
        />
        {hasActiveFilters && (
          <Button
            size="small"
            startIcon={<FilterListOffIcon />}
            onClick={resetParams}
            sx={{ ml: 2 }}
          >
            Filter zurücksetzen
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 600 }}>
          <TableHead>
            {/* Sortierbare Spaltenköpfe */}
            <TableRow>
              <TableCell>{renderSortLabel('name', 'Name')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{renderSortLabel('projekt_typ', 'Typ')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{renderSortLabel('kunde', 'Kunde')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{renderSortLabel('projektleiter', 'Projektleiter')}</TableCell>
              <TableCell>{renderSortLabel('status', 'Status')}</TableCell>
              <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{renderSortLabel('personentage', 'PT', 'center')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{renderSortLabel('erstellt_am', 'Erstellt am')}</TableCell>
            </TableRow>

            {/* Filterzeile */}
            <TableRow>
              {/* Name */}
              <TableCell sx={{ pt: 0.5, pb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Filter..."
                  value={filters.name}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                  sx={filterInputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </TableCell>
              {/* Typ */}
              <TableCell sx={{ pt: 0.5, pb: 1, display: { xs: 'none', md: 'table-cell' } }}>
                <Select
                  size="small"
                  fullWidth
                  displayEmpty
                  value={filters.projekt_typ}
                  onChange={(e) => handleFilterChange('projekt_typ', e.target.value)}
                  sx={{
                    ...filterInputSx,
                    '& .MuiSelect-select': { py: 0.75, fontSize: '0.8125rem' },
                  }}
                >
                  <MenuItem value="">
                    <em>Alle</em>
                  </MenuItem>
                  <MenuItem value="Single">Single</MenuItem>
                  <MenuItem value="Kontingent">Kontingent</MenuItem>
                  <MenuItem value="Festpreis">Festpreis</MenuItem>
                </Select>
              </TableCell>
              {/* Kunde */}
              <TableCell sx={{ pt: 0.5, pb: 1, display: { xs: 'none', sm: 'table-cell' } }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Filter..."
                  value={filters.kunde}
                  onChange={(e) => handleFilterChange('kunde', e.target.value)}
                  sx={filterInputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </TableCell>
              {/* Projektleiter */}
              <TableCell sx={{ pt: 0.5, pb: 1, display: { xs: 'none', md: 'table-cell' } }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Filter..."
                  value={filters.projektleiter}
                  onChange={(e) => handleFilterChange('projektleiter', e.target.value)}
                  sx={filterInputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </TableCell>
              {/* Status */}
              <TableCell sx={{ pt: 0.5, pb: 1 }}>
                <Select
                  size="small"
                  fullWidth
                  displayEmpty
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  sx={{
                    ...filterInputSx,
                    '& .MuiSelect-select': { py: 0.75, fontSize: '0.8125rem' },
                  }}
                >
                  <MenuItem value="">
                    <em>Alle</em>
                  </MenuItem>
                  <MenuItem value="Angebot">Angebot</MenuItem>
                  {statuses.map((s) => (
                    <MenuItem key={s.id} value={s.name}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
              {/* PT */}
              <TableCell sx={{ pt: 0.5, pb: 1, display: { xs: 'none', lg: 'table-cell' } }} align="center">
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Filter..."
                  type="number"
                  value={filters.personentage}
                  onChange={(e) => handleFilterChange('personentage', e.target.value)}
                  sx={filterInputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </TableCell>
              {/* Erstellt am */}
              <TableCell sx={{ pt: 0.5, pb: 1, display: { xs: 'none', lg: 'table-cell' } }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="dd.mm.yyyy"
                  value={filters.erstellt_am}
                  onChange={(e) => handleFilterChange('erstellt_am', e.target.value)}
                  sx={filterInputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {displayedProjekte.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Keine Projekte gefunden
                </TableCell>
              </TableRow>
            ) : (
              displayedProjekte.map((projekt) => (
                <TableRow
                  key={boardRowKey(projekt)}
                  hover
                  {...navigableProps(
                    navigate,
                    projekt.source === 'angebot' ? `/angebote/${projekt.id}` : `/projekte/${projekt.id}`,
                  )}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    {projekt.name}
                    {projekt.source === 'angebot' && projekt.angebotNummer && (
                      <Box component="span" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.75rem' }}>
                        {projekt.angebotNummer}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Chip
                      label={projektTypLabel(projekt.projekt_typ)}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: projektTypAccent(projekt.projekt_typ),
                        color: projektTypAccent(projekt.projekt_typ),
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{projekt.kunde?.name_des_unternehmens || '-'}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {projekt.projektleiter
                      ? `${projekt.projektleiter.vorname} ${projekt.projektleiter.nachname}`
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {projekt.status ? (
                      <Chip
                        label={
                          projekt.source === 'angebot'
                            ? `Angebot · ${angebotPipelineLabel(projekt.angebotPipelineStatus)}`
                            : projekt.status.name
                        }
                        size="small"
                        color={getStatusColor(projekt.status.name) as any}
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    {projekt.source === 'angebot' && isFestpreisProjektTyp(projekt.projekt_typ) && projekt.nettobetrag != null
                      ? `${Number(projekt.nettobetrag).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
                      : projekt.personentage != null
                        ? projekt.personentage.toLocaleString('de-DE', { maximumFractionDigits: 2 })
                        : '-'}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    {format(new Date(projekt.erstellt_am), 'dd.MM.yyyy')}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProjekteListPage;
